/**
 * Volunteer-compensation section for the user detail page (add-volunteer-comp).
 *
 * Sits ABOVE the transaction history. Shows the user's V-day ledger (owed / paid
 * / future) and lets an operator pay out days against the tab — with an
 * ADJUSTABLE day count (capped at the debt) and a "worked today?" toggle — or
 * reverse a mistaken claim. Only renders when the user has V-days on record.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  api,
  ApiError,
  type VolunteerDayRow,
  type VolunteerDaysResponse,
  type VolunteerPreview,
} from "../api/client";

const { Title, Text } = Typography;

function euro(cents: number | null | undefined): string {
  if (cents == null) return "—";
  const sign = cents < 0 ? "-" : "";
  const a = Math.abs(cents);
  return `${sign}€${Math.floor(a / 100)}.${String(a % 100).padStart(2, "0")}`;
}

interface Props {
  strichlisteUserId: number;
  /** Called after a claim/reverse so the parent can refresh the balance. */
  onChanged?: () => void;
  /** Compact banner mode (for the settlement modal): a slim 'N owed days' alert
   * + 'Pay volunteer days' button + the claim modal, no ledger table/stats. */
  compact?: boolean;
}

export const VolunteerDaysSection = ({
  strichlisteUserId,
  onChanged,
  compact,
}: Props) => {
  const [data, setData] = useState<VolunteerDaysResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Claim modal state
  const [claimOpen, setClaimOpen] = useState(false);
  const [preview, setPreview] = useState<VolunteerPreview | null>(null);
  const [daysToPay, setDaysToPay] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getVolunteerDays(strichlisteUserId));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [strichlisteUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The operator picks how many days to pay; the server pays them oldest-first
  // (past → today → future). The max spans ALL unpaid eligible days incl. future,
  // so increasing the number past the debt simply settles ahead into credit — no
  // hidden checkbox to discover.
  const totalEligible = preview
    ? preview.past_eligible_days +
      (preview.today_eligible ? 1 : 0) +
      preview.future_eligible_days
    : 0;
  // Which buckets the oldest-first `n` days fall into, for the live hint.
  const breakdown = (n: number): string => {
    if (!preview) return "";
    const past = Math.min(n, preview.past_eligible_days);
    const today =
      preview.today_eligible && n > preview.past_eligible_days ? 1 : 0;
    const future = Math.max(0, n - past - today);
    const parts: string[] = [];
    if (past) parts.push(`${past} past`);
    if (today) parts.push("today");
    if (future) parts.push(`${future} future`);
    return parts.length ? `Pays ${parts.join(" + ")}` : "Nothing selected";
  };

  const openClaim = async () => {
    try {
      const p = await api.previewVolunteerClaim(strichlisteUserId);
      setPreview(p);
      // Default to the debt-covered amount (past days the debt funds); the
      // operator increases the number to include today / settle ahead.
      setDaysToPay(p.payable_days_excl_today);
      setClaimOpen(true);
    } catch (err) {
      notification.error({
        message: "Could not load volunteer preview",
        description: err instanceof ApiError ? err.message : String(err),
      });
    }
  };

  const submitClaim = async () => {
    setSubmitting(true);
    try {
      const res = await api.claimVolunteer({
        strichliste_user_id: strichlisteUserId,
        // All buckets eligible; the server pays oldest-first up to max_days, and
        // include_future lifts the debt cap so the operator can settle ahead.
        include_today: true,
        include_future: true,
        max_days: daysToPay,
      });
      notification.success({
        message: `Paid ${euro(res.paid_cents)} (${res.paid_day_ids.length} day${res.paid_day_ids.length === 1 ? "" : "s"})`,
      });
      setClaimOpen(false);
      await load();
      onChanged?.();
    } catch (err) {
      notification.error({
        message: "Claim failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reverse = async (claimId: number) => {
    try {
      await api.reverseVolunteer({
        strichliste_user_id: strichlisteUserId,
        claim_id: claimId,
      });
      notification.success({ message: "Claim reversed" });
      await load();
      onChanged?.();
    } catch (err) {
      notification.error({
        message: "Reverse failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    }
  };

  // Don't clutter pages for users with no V-days.
  if (loading || !data || data.days.length === 0) return null;

  const s = data.summary;

  const columns: ColumnsType<VolunteerDayRow> = [
    {
      title: "Date",
      dataIndex: "work_date",
      render: (d: string, row) => (
        <Space size={6}>
          {d}
          {row.is_today ? <Tag color="blue">today</Tag> : null}
        </Space>
      ),
    },
    {
      title: "Status",
      render: (_: unknown, row) =>
        row.paid ? (
          <Tag color="green">paid</Tag>
        ) : row.is_future ? (
          <Tag>scheduled</Tag>
        ) : (
          <Tag color="orange">owed</Tag>
        ),
    },
    {
      title: "Value",
      dataIndex: "value_cents",
      render: (v: number | null) => euro(v),
    },
    {
      title: "",
      render: (_: unknown, row) =>
        row.paid && row.claim_id != null ? (
          <Popconfirm
            title="Reverse this volunteer payout?"
            description="Restores the debt and removes the expense."
            onConfirm={() => reverse(row.claim_id as number)}
            okText="Reverse"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger type="text">
              reverse
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const claimModal = (
    <Modal
      title="Pay volunteer days"
      open={claimOpen}
      onCancel={() => setClaimOpen(false)}
      onOk={submitClaim}
      okText={`Pay ${euro(daysToPay * (preview?.rate_cents ?? 0))}`}
      okButtonProps={{ disabled: daysToPay <= 0, loading: submitting }}
    >
      {preview ? (
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <Text type="secondary">
            Owes {euro(preview.debt_cents)} · rate {euro(preview.rate_cents)}/day ·{" "}
            {totalEligible} unpaid day{totalEligible === 1 ? "" : "s"} available
            {preview.future_eligible_days > 0
              ? ` (incl. ${preview.future_eligible_days} future — increase to settle ahead)`
              : ""}
          </Text>
          <Space>
            <Text>Days to pay:</Text>
            <InputNumber
              min={0}
              max={totalEligible}
              value={daysToPay}
              onChange={(v) => setDaysToPay(typeof v === "number" ? v : 0)}
            />
            <Text type="secondary">of {totalEligible} (oldest first)</Text>
          </Space>
          <Text type="secondary">{breakdown(daysToPay)}</Text>
          {preview.today_eligible &&
          daysToPay <= preview.past_eligible_days ? (
            <Text type="warning">
              ⓘ Today is not included — increase the day count to add it.
            </Text>
          ) : null}
          <Text strong>
            = {euro(daysToPay * preview.rate_cents)}
            {daysToPay * preview.rate_cents > preview.debt_cents
              ? ` — settles the tab + ${euro(
                  daysToPay * preview.rate_cents - preview.debt_cents,
                )} ahead (credit)`
              : " off the tab"}
          </Text>
        </Space>
      ) : null}
    </Modal>
  );

  // Compact banner mode — for embedding in the settlement modal.
  if (compact) {
    if (s.owed_count === 0 && s.future_count === 0) return null;
    const label =
      s.owed_count > 0
        ? `${s.owed_count} unpaid volunteer day${s.owed_count === 1 ? "" : "s"} (${euro(s.owed_value_cents)} owed)`
        : `${s.future_count} future volunteer day${s.future_count === 1 ? "" : "s"} — settle ahead?`;
    return (
      <>
        <Alert
          type={s.owed_count > 0 ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space wrap>
              <span>{label}</span>
              <Button size="small" type="primary" onClick={openClaim}>
                Pay volunteer days
              </Button>
            </Space>
          }
        />
        {claimModal}
      </>
    );
  }

  return (
    <Card
      size="small"
      style={{ marginTop: 24 }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          Volunteer days
        </Title>
      }
      extra={
        s.owed_count > 0 || s.future_count > 0 ? (
          <Button type="primary" onClick={openClaim}>
            Pay volunteer days
          </Button>
        ) : null
      }
    >
      <Space size={48} style={{ marginBottom: 12 }} wrap>
        <Statistic
          title="Owed (worked, unpaid)"
          value={euro(s.owed_value_cents)}
          suffix={`· ${s.owed_count} day${s.owed_count === 1 ? "" : "s"}`}
        />
        <Statistic
          title="Paid out"
          value={euro(s.paid_value_cents)}
          suffix={`· ${s.paid_count} day${s.paid_count === 1 ? "" : "s"}`}
        />
        {s.future_count > 0 ? (
          <Statistic title="Scheduled (future)" value={s.future_count} />
        ) : null}
      </Space>
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data.days}
        pagination={false}
      />

      {claimModal}
    </Card>
  );
};
